// preload.ts - Request permissions for protected environments (sandboxes)

/**
 * Check if we're running in a sandbox environment by looking at the URL
 */
export const isInSandbox = (): boolean => {
  try {
    return window.location.href.toLowerCase().includes('sandbox');
  } catch {
    return false;
  }
};

/**
 * Generic permission request function
 * Makes lightweight HEAD requests to establish permissions in sandboxed environments
 */
const requestPermissions = async (endpoints: string[]): Promise<void> => {
  try {
    // Make lightweight HEAD requests to establish permissions
    const permissionRequests = endpoints.map(async (endpoint) => {
      try {
        // Use a very lightweight request that should be allowed by CORS
        await fetch(endpoint, {
          method: 'HEAD',
          mode: 'no-cors', // This allows the request even if CORS isn't set up
          cache: 'no-store',
          referrerPolicy: 'no-referrer',
        });
      } catch (error) {
        // Silently ignore failures - we just want to establish permissions
        console.debug(`Permission request failed for ${endpoint}:`, error instanceof Error ? error.message : 'Unknown error');
      }
    });

    // Wait for all permission requests to complete (or fail)
    await Promise.allSettled(permissionRequests);

    console.debug('[SubscribeDev Preload] Permission requests completed for:', endpoints);

  } catch (error) {
    console.warn('[SubscribeDev Preload] Failed to request permissions:', error);
    // Don't throw - this is just a best-effort preload
  }
};

/**
 * Preload Subscribe.dev endpoints after user signs in
 * Only runs in sandbox environments (when URL contains "sandbox")
 */
export const preloadSubscribeDevPermissions = async (): Promise<void> => {
  // Only preload in sandbox environments
  if (!isInSandbox()) {
    console.debug('[SubscribeDev Preload] Not in sandbox environment, skipping preload');
    return;
  }

  console.debug('[SubscribeDev Preload] Sandbox environment detected, preloading permissions');

  const subscribeDevEndpoints = [
    'https://api.subscribe.dev/v1/users/profile',
    'https://images.subscribe.dev/uploads/',
    'https://img.clerk.com',
  ];

  await requestPermissions(subscribeDevEndpoints);
};
